/**
 * Identification & Classification Section Component
 * POV-0071: Build Identification & Classification section UI
 * 
 * Per PRD Section 10.2, displays:
 * - Classification (Commodity, Product Line, Criticality)
 * - Regulatory Flags (ITAR, EAR, Export License)
 * - Customer Identifiers (table with add/remove)
 * - Traceability Policy (requirement, lot attributes)
 * - Preservation (shelf life, storage conditions)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { 
  Shield, 
  AlertTriangle, 
  Users, 
  Package, 
  Clock,
  Plus,
  Trash2,
  Lock,
  Loader2,
  Info,
  CheckCircle2,
  XCircle
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { ViewRelatedHistoryLink } from './HistorySection';
import { Button } from '../ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { toast } from 'sonner';
import api from '../../services/api';

/**
 * Criticality level options
 */
const CRITICALITY_OPTIONS = [
  { value: 'normal', label: 'Normal' },
  { value: 'key_characteristic', label: 'Key Characteristic' },
  { value: 'safety_critical', label: 'Safety Critical' },
];

/**
 * Traceability requirement options
 */
const TRACEABILITY_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'lot', label: 'Lot Controlled' },
  { value: 'serial', label: 'Serial Controlled' },
];

/**
 * Reason codes for controlled field changes
 */
const REASON_CODES = [
  { value: 'engineering_change', label: 'Engineering Change' },
  { value: 'quality_requirement', label: 'Quality Requirement' },
  { value: 'customer_requirement', label: 'Customer Requirement' },
  { value: 'regulatory_change', label: 'Regulatory Change' },
  { value: 'risk_assessment_update', label: 'Risk Assessment Update' },
  { value: 'process_improvement', label: 'Process Improvement' },
  { value: 'data_correction', label: 'Data Correction' },
  { value: 'initial_setup', label: 'Initial Setup' },
  { value: 'other', label: 'Other' },
];

/**
 * Section card wrapper
 */
const SectionCard = ({ title, icon: Icon, children, className, controlled = false }) => (
  <div className={cn("bg-slate-50 rounded-lg p-4 border border-slate-100", className)}>
    <div className="flex items-center justify-between mb-3">
      <h4 className="flex items-center gap-2 text-xs font-semibold text-slate-600 uppercase tracking-wider">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        {title}
      </h4>
      {controlled && (
        <span className="flex items-center gap-1 text-xs text-amber-600">
          <Lock className="w-3 h-3" />
          Controlled
        </span>
      )}
    </div>
    {children}
  </div>
);

/**
 * AS9100D Guidance Banner
 */
const AS9100DGuidance = () => (
  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4" data-testid="as9100d-guidance">
    <div className="flex items-start gap-2">
      <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
      <div className="text-xs text-blue-700">
        <p className="font-medium">AS9100D Clause 8.5.2</p>
        <p className="mt-1">Identify product by suitable means throughout production and service provision.</p>
      </div>
    </div>
  </div>
);

/**
 * Reason for Change Dialog
 */
const ReasonForChangeDialog = ({ 
  open, 
  onOpenChange, 
  onConfirm, 
  fieldName,
  isSubmitting 
}) => {
  const [reasonCode, setReasonCode] = useState('');
  const [comment, setComment] = useState('');

  const handleConfirm = () => {
    if (!reasonCode) {
      toast.error('Please select a reason code');
      return;
    }
    if (reasonCode === 'other' && !comment.trim()) {
      toast.error('Comment is required when reason is "Other"');
      return;
    }
    onConfirm(reasonCode, comment);
  };

  const handleOpenChange = (open) => {
    if (!open) {
      setReasonCode('');
      setComment('');
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="reason-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="w-4 h-4 text-amber-600" />
            Controlled Change
          </DialogTitle>
          <DialogDescription>
            You are modifying <span className="font-medium">{fieldName}</span>, a controlled field.
            Please provide a reason for this change.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="reason-code">Reason Code *</Label>
            <Select value={reasonCode} onValueChange={setReasonCode}>
              <SelectTrigger id="reason-code" data-testid="reason-code-select">
                <SelectValue placeholder="Select a reason..." />
              </SelectTrigger>
              <SelectContent>
                {REASON_CODES.map((reason) => (
                  <SelectItem key={reason.value} value={reason.value}>
                    {reason.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="comment">
              Comment {reasonCode === 'other' ? '*' : '(optional)'}
            </Label>
            <Textarea
              id="comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Provide additional context..."
              rows={3}
              data-testid="reason-comment"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Confirm Change
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Add Customer Identifier Dialog
 */
const AddCustomerDialog = ({ open, onOpenChange, onAdd, isSubmitting }) => {
  const [customerName, setCustomerName] = useState('');
  const [customerPartNumber, setCustomerPartNumber] = useState('');
  const [drawingRef, setDrawingRef] = useState('');

  const handleAdd = () => {
    if (!customerName.trim() || !customerPartNumber.trim()) {
      toast.error('Customer name and part number are required');
      return;
    }
    onAdd({
      customer_name: customerName.trim(),
      customer_part_number: customerPartNumber.trim(),
      customer_drawing_ref: drawingRef.trim() || null,
    });
  };

  const handleOpenChange = (open) => {
    if (!open) {
      setCustomerName('');
      setCustomerPartNumber('');
      setDrawingRef('');
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="add-customer-dialog">
        <DialogHeader>
          <DialogTitle>Add Customer Identifier</DialogTitle>
          <DialogDescription>
            Add a customer-specific part number for this part.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="customer-name">Customer Name *</Label>
            <Input
              id="customer-name"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              placeholder="e.g., Acme Corp"
              data-testid="customer-name-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="customer-pn">Customer Part Number *</Label>
            <Input
              id="customer-pn"
              value={customerPartNumber}
              onChange={(e) => setCustomerPartNumber(e.target.value)}
              placeholder="e.g., ACM-CPU-1511"
              data-testid="customer-pn-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="drawing-ref">Drawing Reference (optional)</Label>
            <Input
              id="drawing-ref"
              value={drawingRef}
              onChange={(e) => setDrawingRef(e.target.value)}
              placeholder="e.g., DWG-12345"
              data-testid="drawing-ref-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleAdd} disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Add Customer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Main Identification Section Component
 */
const IdentificationSection = ({ partId, onViewRelatedHistory }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Dialog states
  const [reasonDialogOpen, setReasonDialogOpen] = useState(false);
  const [pendingChange, setPendingChange] = useState(null);
  const [addCustomerDialogOpen, setAddCustomerDialogOpen] = useState(false);

  // Fetch identification attributes
  const fetchData = useCallback(async () => {
    if (!partId) return;
    
    try {
      setLoading(true);
      const response = await api.get(`/api/v1/identification/${partId}`);
      setData(response.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to fetch identification attributes');
      console.error('Error fetching identification:', err);
    } finally {
      setLoading(false);
    }
  }, [partId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Update attributes
  const updateAttributes = async (updates, reasonCode = null, comment = null) => {
    try {
      setIsSubmitting(true);
      
      const body = {
        ...updates,
        ...(reasonCode && { reason_code: reasonCode }),
        ...(comment && { comment }),
      };
      
      const response = await api.put(`/api/v1/identification/${partId}`, body);
      setData(prev => ({ ...prev, ...response.data }));
      toast.success('Attributes updated successfully');
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Failed to update attributes');
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle controlled field change (needs reason)
  const handleControlledChange = (fieldName, value) => {
    setPendingChange({ fieldName, value });
    setReasonDialogOpen(true);
  };

  // Confirm controlled change with reason
  const handleConfirmControlledChange = async (reasonCode, comment) => {
    if (!pendingChange) return;
    
    const { fieldName, value } = pendingChange;
    const success = await updateAttributes(
      { [fieldName]: value },
      reasonCode,
      comment
    );
    
    if (success) {
      setReasonDialogOpen(false);
      setPendingChange(null);
    }
  };

  // Handle non-controlled field change
  const handleFieldChange = async (fieldName, value) => {
    await updateAttributes({ [fieldName]: value });
  };

  // Handle regulatory flag change
  const handleRegulatoryFlagChange = async (flag, checked) => {
    const newFlags = {
      ...data?.regulatory_flags,
      [flag]: checked,
    };
    await updateAttributes({ regulatory_flags: newFlags });
  };

  // Handle lot attribute change
  const handleLotAttributeChange = (attr, checked) => {
    const newAttrs = {
      ...data?.required_lot_attributes,
      [attr]: checked,
    };
    handleControlledChange('required_lot_attributes', newAttrs);
  };

  // Add customer identifier
  const handleAddCustomer = async (customerData) => {
    try {
      setIsSubmitting(true);
      
      const response = await api.post(
        `/api/v1/identification/${partId}/customer-identifiers`,
        customerData
      );
      
      setData(prev => ({ ...prev, ...response.data }));
      setAddCustomerDialogOpen(false);
      toast.success('Customer identifier added');
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Failed to add customer identifier');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Remove customer identifier
  const handleRemoveCustomer = async (customerName, customerPartNumber) => {
    try {
      setIsSubmitting(true);
      
      const response = await api.delete(
        `/api/v1/identification/${partId}/customer-identifiers`,
        {
          data: {
            customer_name: customerName,
            customer_part_number: customerPartNumber,
          }
        }
      );
      
      setData(prev => ({ ...prev, ...response.data }));
      toast.success('Customer identifier removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Failed to remove customer identifier');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8" data-testid="identification-loading">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        <span className="ml-2 text-sm text-slate-500">Loading identification data...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg" data-testid="identification-error">
        <XCircle className="w-5 h-5 text-red-500" />
        <span className="text-sm text-red-700">{error}</span>
        <Button variant="ghost" size="sm" onClick={fetchData}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="identification-section-content">
      {/* AS9100D Guidance */}
      <AS9100DGuidance />

      {/* Classification Section */}
      <SectionCard title="Classification" icon={Package}>
        <div className="grid grid-cols-2 gap-4">
          {/* Commodity */}
          <div className="space-y-1">
            <Label htmlFor="commodity" className="text-xs text-slate-500">Commodity</Label>
            <Input
              id="commodity"
              value={data?.commodity || ''}
              onChange={(e) => handleFieldChange('commodity', e.target.value || null)}
              placeholder="e.g., Electronic Components"
              className="h-8 text-sm"
              data-testid="commodity-input"
            />
          </div>
          
          {/* Product Line */}
          <div className="space-y-1">
            <Label htmlFor="product-line" className="text-xs text-slate-500">Product Line</Label>
            <Input
              id="product-line"
              value={data?.product_line || ''}
              onChange={(e) => handleFieldChange('product_line', e.target.value || null)}
              placeholder="e.g., Industrial Automation"
              className="h-8 text-sm"
              data-testid="product-line-input"
            />
          </div>
          
          {/* Criticality (Controlled) */}
          <div className="space-y-1 col-span-2">
            <div className="flex items-center gap-2">
              <Label htmlFor="criticality" className="text-xs text-slate-500">Criticality</Label>
              <Lock className="w-3 h-3 text-amber-500" />
              <span className="text-xs text-amber-600">Controlled</span>
            </div>
            <Select
              value={data?.criticality || 'normal'}
              onValueChange={(value) => handleControlledChange('criticality', value)}
            >
              <SelectTrigger id="criticality" className="h-8 text-sm" data-testid="criticality-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CRITICALITY_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </SectionCard>

      {/* Regulatory Flags Section */}
      <SectionCard title="Regulatory Flags" icon={Shield}>
        <div className="flex flex-wrap gap-6">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="itar"
              checked={data?.regulatory_flags?.itar || false}
              onCheckedChange={(checked) => handleRegulatoryFlagChange('itar', checked)}
              data-testid="itar-checkbox"
            />
            <Label htmlFor="itar" className="text-sm cursor-pointer">ITAR Controlled</Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="ear"
              checked={data?.regulatory_flags?.ear || false}
              onCheckedChange={(checked) => handleRegulatoryFlagChange('ear', checked)}
              data-testid="ear-checkbox"
            />
            <Label htmlFor="ear" className="text-sm cursor-pointer">EAR Controlled</Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="export-license"
              checked={data?.regulatory_flags?.export_license || false}
              onCheckedChange={(checked) => handleRegulatoryFlagChange('export_license', checked)}
              data-testid="export-license-checkbox"
            />
            <Label htmlFor="export-license" className="text-sm cursor-pointer">Export License Required</Label>
          </div>
        </div>
      </SectionCard>

      {/* Customer Identifiers Section */}
      <SectionCard title="Customer Identifiers" icon={Users}>
        {data?.customer_identifiers?.length > 0 ? (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Customer</TableHead>
                  <TableHead className="text-xs">Customer P/N</TableHead>
                  <TableHead className="text-xs">Drawing Ref</TableHead>
                  <TableHead className="text-xs w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.customer_identifiers.map((ci, idx) => (
                  <TableRow key={idx} data-testid={`customer-row-${idx}`}>
                    <TableCell className="text-sm">{ci.customer_name}</TableCell>
                    <TableCell className="text-sm font-mono">{ci.customer_part_number}</TableCell>
                    <TableCell className="text-sm text-slate-500">{ci.customer_drawing_ref || '—'}</TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-slate-400 hover:text-red-500"
                        onClick={() => handleRemoveCustomer(ci.customer_name, ci.customer_part_number)}
                        disabled={isSubmitting}
                        data-testid={`remove-customer-${idx}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <p className="text-sm text-slate-500 italic">No customer identifiers defined</p>
        )}
        <Button
          variant="outline"
          size="sm"
          className="mt-3"
          onClick={() => setAddCustomerDialogOpen(true)}
          data-testid="add-customer-btn"
        >
          <Plus className="w-4 h-4 mr-1" />
          Add Customer ID
        </Button>
      </SectionCard>

      {/* Traceability Policy Section */}
      <SectionCard title="Traceability Policy" icon={AlertTriangle} controlled>
        <div className="space-y-4">
          {/* Traceability Requirement */}
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Label htmlFor="traceability" className="text-xs text-slate-500">Traceability Requirement</Label>
              <Lock className="w-3 h-3 text-amber-500" />
            </div>
            <Select
              value={data?.traceability_requirement || 'none'}
              onValueChange={(value) => handleControlledChange('traceability_requirement', value)}
            >
              <SelectTrigger id="traceability" className="h-8 text-sm w-full max-w-xs" data-testid="traceability-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TRACEABILITY_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Required Lot Attributes (shown when traceability is lot or serial) */}
          {data?.traceability_requirement && data.traceability_requirement !== 'none' && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label className="text-xs text-slate-500">Required Lot Attributes</Label>
                <Lock className="w-3 h-3 text-amber-500" />
              </div>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="heat-lot"
                    checked={data?.required_lot_attributes?.heat_lot || false}
                    onCheckedChange={(checked) => handleLotAttributeChange('heat_lot', checked)}
                    data-testid="heat-lot-checkbox"
                  />
                  <Label htmlFor="heat-lot" className="text-sm cursor-pointer">Heat/Lot Number</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="mfg-date"
                    checked={data?.required_lot_attributes?.mfg_date || false}
                    onCheckedChange={(checked) => handleLotAttributeChange('mfg_date', checked)}
                    data-testid="mfg-date-checkbox"
                  />
                  <Label htmlFor="mfg-date" className="text-sm cursor-pointer">Mfg Date</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="expiry-date"
                    checked={data?.required_lot_attributes?.expiry_date || false}
                    onCheckedChange={(checked) => handleLotAttributeChange('expiry_date', checked)}
                    data-testid="expiry-date-checkbox"
                  />
                  <Label htmlFor="expiry-date" className="text-sm cursor-pointer">Expiry Date</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="batch"
                    checked={data?.required_lot_attributes?.batch || false}
                    onCheckedChange={(checked) => handleLotAttributeChange('batch', checked)}
                    data-testid="batch-checkbox"
                  />
                  <Label htmlFor="batch" className="text-sm cursor-pointer">Batch #</Label>
                </div>
              </div>
            </div>
          )}
        </div>
      </SectionCard>

      {/* Preservation Section */}
      <SectionCard title="Preservation" icon={Clock}>
        <div className="space-y-4">
          {/* Shelf Life Controlled */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="shelf-life"
              checked={data?.shelf_life_controlled || false}
              onCheckedChange={(checked) => handleFieldChange('shelf_life_controlled', checked)}
              data-testid="shelf-life-checkbox"
            />
            <Label htmlFor="shelf-life" className="text-sm cursor-pointer">Shelf-Life Controlled</Label>
          </div>
          
          {/* Storage Conditions */}
          <div className="space-y-1">
            <Label htmlFor="storage" className="text-xs text-slate-500">Storage Conditions</Label>
            <Textarea
              id="storage"
              value={data?.storage_conditions || ''}
              onChange={(e) => handleFieldChange('storage_conditions', e.target.value || null)}
              placeholder="e.g., Standard warehouse conditions, 15-25°C, <60% RH"
              rows={2}
              className="text-sm"
              data-testid="storage-conditions-input"
            />
          </div>
        </div>
      </SectionCard>

      {/* View Related History Link */}
      <ViewRelatedHistoryLink 
        sectionKey="identification_classification" 
        onClick={onViewRelatedHistory} 
      />

      {/* Reason for Change Dialog */}
      <ReasonForChangeDialog
        open={reasonDialogOpen}
        onOpenChange={setReasonDialogOpen}
        onConfirm={handleConfirmControlledChange}
        fieldName={pendingChange?.fieldName?.replace(/_/g, ' ')}
        isSubmitting={isSubmitting}
      />

      {/* Add Customer Dialog */}
      <AddCustomerDialog
        open={addCustomerDialogOpen}
        onOpenChange={setAddCustomerDialogOpen}
        onAdd={handleAddCustomer}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

export default IdentificationSection;
